import { AutoStack, FlexSpinner, onFormatDate } from 'components'
import { StatusIcon } from 'components/CustomIcons/StatusIcons'
import { EntityList, renderDate, renderEnum } from 'components/EntityList'
import { FieldProps } from 'formik'
import { Box } from 'grid'
import { getParticipantDisplayName, omitDeep } from 'helpers'
import { useCurrentProving } from 'hooks/useCurrentProving'
import useSelection from 'hooks/useSelection'
import { noop } from 'lodash'
import { Participant, Proving, User } from 'model'
import {
  ActionButton,
  CheckboxVisibility,
  Label,
  MarqueeSelection,
  SelectionMode,
  Stack,
  Text,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
import { useParticipantListPickerQuery } from './ParticipantListPicker.generated'

interface DoubleBookingInfo {
  participant: Participant
  provingGroundTitle: string
  provingDescription: string
  provingNr: string
}

export const FormikParticipantByProvingPicker: React.FC<FieldProps<any>> = ({
  field,
  form,
}) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const { data, loading, error } = useParticipantListPickerQuery()
  const provings = data && data.provings ? data.provings : []
  const [selectedProving, setSelectedProving] = React.useState<Proving>()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])

  const onSelectionChanged = (selectedParticipants: Participant[]) => {
    form.setFieldValue(
      field.name,
      selectedParticipants.map((p) => ({
        personId: p.person ? p.person._id : undefined,
        percentage: p.percentage || undefined,
        unknownPerson: omitDeep(p.unknownPerson, ['__typename']),
      }))
    )
  }

  const [selection, selectedItems] = useSelection<Participant>({
    onSelectionChanged,
    getKey: (item) => item._id,
  })

  const handleBackButtonClick = React.useCallback(() => {
    selection.setAllSelected(false)
    setSelectedProving(undefined)
  }, [selection, setSelectedProving])

  const doubleBookings: DoubleBookingInfo[] = []
  const personIds = selectedItems.map((p) => (p.person ? p.person._id : ''))
  provings.forEach((prov) => {
    ;(prov.participants || []).forEach((p) => {
      if (p.person && p.person._id && personIds.includes(p.person._id)) {
        if (p.dateFrom <= proving.dateTo && p.dateTo >= proving.dateFrom) {
          doubleBookings.push({
            participant: p,
            provingGroundTitle: prov.provingGround
              ? prov.provingGround.title
              : '',
            provingNr: prov.id,
            provingDescription: prov.title,
          })
        }
      }
    })
  })

  const provingColumns = [
    fieldColumn('status', {
      onRender: (item, i, col) => (
        <span>
          <StatusIcon value={item} /> {t(`enum.status.${item.status}`)}
        </span>
      ),
      minWidth: 150,
    }),
    fieldColumn('id', { isSorted: true, isSortedDescending: true }),
    fieldColumn('title'),
    fieldColumn('provingGround', {
      onRender: (item: Proving) =>
        item.provingGround ? item.provingGround.title : '',
    }),
    fieldColumn('dateFrom', { onRender: renderDate }),
    fieldColumn('dateTo', { onRender: renderDate }),
    fieldColumn('responsible', {
      onRender: (item: Proving) => {
        return (
          <div>
            {item.responsible.map((user: User, a: any) => {
              return (
                <Box key={a} style={{ whiteSpace: 'pre-line' }}>
                  {user.name}
                </Box>
              )
            })}
          </div>
        )
      },
    }),
    fieldColumn('department'),
  ]

  const participantColumns = [
    fieldColumn('person.type', {
      minWidth: 120,
      isGrouped: true,
      getGroupValue: (item: Participant) =>
        item.unknownPerson
          ? t(`enum.personType.${item.unknownPerson.personType}`)
          : item && item.person && (item.person as User).external
          ? 'DVT/WVT'
          : t(`enum.personType.${item.person!.type}`),
      onRender: (item: Participant, i, col) =>
        item.unknownPerson
          ? t(`enum.personType.${item.unknownPerson.personType}`)
          : item && item.person && (item.person as User).external
          ? 'DVT/WVT'
          : renderEnum(t, 'personType')(item, i, col),
      useFullFieldname: true,
    }),
    fieldColumn('person', {
      isSorted: true,
      customFieldName: 'Name',
      minWidth: 200,
      onRender: (p: Participant) => getParticipantDisplayName(p),
    }),
    fieldColumn('dateFrom', { onRender: renderDate }),
    fieldColumn('dateTo', { onRender: renderDate }),
  ]

  return (
    <FlexSpinner
      loading={loading}
      error={error}
      label={'Daten werden geladen...'}
      labelPosition={'bottom'}
    >
      {!selectedProving && (
        <>
          <Label>{'Bitte wählen Sie die gewünschte Erprobung aus.'}</Label>
          <EntityList
            items={provings.filter((p) => p.id !== proving.id)}
            columns={provingColumns}
            compact={true}
            // onColumnHeaderClick={() => {
            //   return noop
            // }}
            disableListStyleToggle={true}
            checkboxVisibility={CheckboxVisibility.hidden}
            onItemInvoked={(item?: Proving) => {
              if (item) {
                setSelectedProving(item)
              }
            }}
          />
        </>
      )}

      <Stack horizontal={true} tokens={{ childrenGap: 8 }}>
        {selectedProving && (
          <Stack.Item
            styles={{
              root: { width: doubleBookings.length > 0 ? '70%' : '100%' },
            }}
          >
            <AutoStack
              horizontal={true}
              tokens={{ childrenGap: 16 }}
              horizontalAlign={'space-between'}
            >
              <Label>
                {'Bitte wählen Sie die gewünschten Teilnehmer aus.'}
              </Label>
              <ActionButton
                iconProps={{ iconName: 'Back' }}
                onClick={handleBackButtonClick}
                text={'Andere Erprobung auswählen'}
              />
            </AutoStack>

            <MarqueeSelection selection={selection}>
              <EntityList
                items={selectedProving.participants || []}
                columns={participantColumns}
                compact={true}
                selection={selection}
                selectionMode={SelectionMode.multiple}
                selectionPreservedOnEmptyClick={true}
                onColumnHeaderClick={() => {
                  return noop
                }}
                onRenderRow={(props, defaultRender?) => (
                  <div data-selection-toggle="true">
                    {defaultRender && defaultRender(props)}
                  </div>
                )}
                disableListStyleToggle={true}
              />
            </MarqueeSelection>
          </Stack.Item>
        )}
        {doubleBookings.length > 0 && (
          <Stack.Item styles={{ root: { width: '30%' } }}>
            <Stack>
              <Text variant="xLarge" as="h2">
                ACHTUNG!
              </Text>
              <Stack.Item>
                <Label>
                  {t(
                    `Folgende Teilnehmer sind zum Zeitraum ${onFormatDate(
                      new Date(proving.dateFrom)
                    )} bis ${onFormatDate(
                      new Date(proving.dateTo)
                    )} bereits gebucht`
                  )}
                </Label>
                {doubleBookings.map((x: DoubleBookingInfo, i: number) => {
                  return (
                    <Stack key={i}>
                      <Label>
                        {x.participant.person ? x.participant.person.name : ''}
                      </Label>
                      <table>
                        <tbody>
                          <tr>
                            <td>Name: {x.participant.displayName}</td>
                          </tr>
                          <tr>
                            <td>Standort: {x.provingGroundTitle}</td>
                          </tr>
                          <tr>
                            <td>Erprobungsnr.: {x.provingNr}</td>
                          </tr>
                          <tr>
                            <td>Erprobung: {x.provingDescription}</td>
                          </tr>
                          <tr>
                            <td>
                              Abteilung:{' '}
                              {x.participant.person
                                ? x.participant.person.department
                                : ''}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              Datum:{' '}
                              {onFormatDate(new Date(x.participant.dateFrom))}{' '}
                              bis {onFormatDate(new Date(x.participant.dateTo))}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <hr />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Stack>
                  )
                })}
                <Label>
                  {t('Möchten Sie die Teilnehmer trotzdem buchen?')}
                </Label>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}
      </Stack>
    </FlexSpinner>
  )
}
